import { ISubscriptions, IPartnerInquiries, ISubscriptionsHasBuildings } from '@llws/lift-entity-interfaces';
import dayjs from 'dayjs';
import { Equal, GreaterThanOrEqual, IQueryCommandOptions } from '@llws/dynamic-router-utils';
import {
  IApiResponse,
  IMetaResponse,
} from '@/shared';
import { IAction } from '@/shared/interfaces/store-base';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { StoreBaseImplementationV2 } from '../../shared/classes/store-base-implementation-v2';

// Base store configuration
const adminGatewayResourcePath: string = '/syndication-api/subscriptions';
// const subscriptionsPath: string = '/syndication-api/subscriptions';
const listViewName: string = '';
const showLogging: boolean = false;

interface IPromotedListingTransferResponse {
  currentSubscription: ISubscriptions;
  insertedRecords: {
    subscriptions: ISubscriptions[];
    subscriptionsHasBuildings: ISubscriptionsHasBuildings[];
    partnerInquiries: IPartnerInquiries[];
  }
}

/**
 * Get Filters for active Promoted Listings Contract subscriptions for a client.
 */
const getClientPromotedListingsFilters: IQueryCommandOptions = {
  alias: 's',
  select: [
    'b.buildingName',
    'b.streetNumber',
    'b.streetName',
    'gc.cityName',
    'p.name',
    'lplt.name',
    's.id',
    's.startDate',
    's.expiryDate',
  ],
  relations: [
    {
      path: 's.subscriptionsHasBuildings',
      alias: 'shb',
    },
    {
      path: 'shb.building',
      alias: 'b',
    },
    {
      path: 'b.city',
      alias: 'gc',
    },
    {
      path: 's.baseSubscription',
      alias: 'bs',
    },
    {
      path: 'bs.listingPromotedListingTypes',
      alias: 'lplt',
    },
    {
      path: 'bs.partner',
      alias: 'p',
    },
    {
      path: 'bs.service',
      alias: 'se',
    },
  ],
  where: [
    Equal('se.name', 'Promoted Listings'),
    {
      condition: 'and',
      whereCommands: [
        {
          field: 's.expiryDate',
          operator: 'null',
          value: '',
          condition: 'or',
          notOperator: false,
        },
        {
          ...GreaterThanOrEqual('s.expiryDate', dayjs().format('YYYY-MM-DD')),
          condition: 'or',
        },
      ],
    },
  ],
  limit: 9999,
};

const defaultGetListFilters: IQueryCommandOptions = {
  alias: 's',
  select: [
    'b.buildingName',
    'b.streetNumber',
    'b.streetName',
    'gc.cityName',
    'p.name',
    'lplt.name',
    's.id',
    's.startDate',
    's.minDuration',
    's.expiryDate',
  ],
  relations: [
    {
      path: 's.subscriptionsHasBuildings',
      alias: 'shb',
    },
    {
      path: 'shb.building',
      alias: 'b',
    },
    {
      path: 'b.city',
      alias: 'gc',
    },
    {
      path: 's.baseSubscription',
      alias: 'bs',
    },
    {
      path: 'bs.listingPromotedListingTypes',
      alias: 'lplt',
    },
    {
      path: 'bs.partner',
      alias: 'p',
    },
    {
      path: 'bs.service',
      alias: 'se',
    },
  ],
  where: [
    Equal('se.name', 'Promoted Listings'),
    {
      condition: 'and',
      whereCommands: [
        {
          field: 's.expiryDate',
          operator: 'null',
          value: '',
          condition: 'or',
          notOperator: false,
        },
        {
          ...GreaterThanOrEqual('s.expiryDate', dayjs().format('YYYY-MM-DD')),
          condition: 'or',
        },
      ],
    },
  ],
  limit: 9999,
};

const defaultGetSingleFilters: IQueryCommandOptions = {
  alias: 's',
  select: [
    'b.buildingName',
    'b.streetNumber',
    'b.streetName',
    'gc.cityName',
    'p.name',
    'lplt.name',
    's.id',
    's.startDate',
    's.minDuration',
    's.expiryDate',
  ],
  relations: [
    {
      path: 's.subscriptionsHasBuildings',
      alias: 'shb',
    },
    {
      path: 'shb.building',
      alias: 'b',
    },
    {
      path: 'b.city',
      alias: 'gc',
    },
    {
      path: 's.baseSubscription',
      alias: 'bs',
    },
    {
      path: 'bs.listingPromotedListingTypes',
      alias: 'lplt',
    },
    {
      path: 'bs.partner',
      alias: 'p',
    },
    {
      path: 'bs.service',
      alias: 'se',
    },
  ],
  where: [
    Equal('se.name', 'Promoted Listings'),
    {
      condition: 'and',
      whereCommands: [
        {
          field: 's.expiryDate',
          operator: 'null',
          value: '',
          condition: 'or',
          notOperator: false,
        },
        {
          ...GreaterThanOrEqual('s.expiryDate', dayjs().format('YYYY-MM-DD')),
          condition: 'or',
        },
      ],
    },
  ],
};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<ISubscriptions>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

interface IPromotedListingsState {
  list: IApiResponse<ISubscriptions[], IMetaResponse> | null;
  single: IApiResponse<ISubscriptions, IMetaResponse> | null;
}

interface IPromotedListingsPayload {
  subscription: ISubscriptions;
  subscriptionsHasBuildings: ISubscriptionsHasBuildings;
  partnerInquiry: IPartnerInquiries;
}

interface IPromotedListingsTransferPayload {
  subscriptionId: number;
  propertyId: number;
  partnerInquiry: IPartnerInquiries;
  transferDate?: string;
}

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'promoted-listings/'

  state: {
    ...base.state,
  },

  getters: {
    ...base.getters,
  },

  mutations: {
    ...base.mutations,
  },

  actions: {
    ...base.actions,

    addPromotedListings: async (ctx: IAction<IPromotedListingsState>, promotedListings: IPromotedListingsPayload[]) => {
      const result = await adminRequest.post(
        '/syndication-api/promoted_listings',
        promotedListings,
      );
      return result;
    },

    transferPromotedListing: async (ctx: IAction<IPromotedListingsState>, promotedListings: IPromotedListingsTransferPayload) => {
      const result = await adminRequest.post(
        '/syndication-api/promoted_listing_transfers',
        promotedListings,
      ) as IApiResponse<IPromotedListingTransferResponse, any>;
      const partnerInquiries = result.data?.insertedRecords.partnerInquiries;
      ctx.commit('partner-inquiries/appendToList', partnerInquiries, { root: true });
      return result;
    },
  },

};
